import React, { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Landmark, Lock, CheckCircle2, IdCard, FileText, Headphones, Clock, ArrowRight, Search } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { PortalShell } from "@/components/portal/PortalShell";
import { WelcomeVideo } from "@/components/portal/WelcomeVideo";
import { ClientPaymentProcess } from "@/components/portal/ClientPaymentProcess";
import { ClientDocumentsSection } from "@/components/portal/ClientDocumentsSection";
import { SupportContacts } from "@/components/portal/SupportContacts";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";

function Field({ label, value, mono }) {
  return (<div><dt className="tech-label text-muted-foreground">{label}</dt><dd className={`mt-1 text-sm text-foreground ${mono ? "font-mono" : ""}`}>{value}</dd></div>);
}
function SectionWrap({ title, Icon, children, verified, verifiedLabel }) {
  return (
    <div className="mx-auto max-w-4xl">
      <Card className="premium-card overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2.5"><Icon className="h-5 w-5 text-accent" /><h2 className="font-medium text-foreground">{title}</h2></div>
          {verified && <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" />{verifiedLabel}</span>}
        </div>
        {children}
      </Card>
    </div>
  );
}
const Empty = ({ label }) => <p className="px-5 py-12 text-center text-sm text-muted-foreground">{label}</p>;

export function ClientDashboard({ user }) {
  const { t, lang } = useLanguage();
  const [section, setSection] = useState("overview");
  const [transactions, setTransactions] = useState([]);
  const [bank, setBank] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [profile, setProfile] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [activeTx, setActiveTx] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [txs, b, i, p] = await Promise.all([
      api.get("/me/transactions").then((r) => r.data),
      api.get("/me/bank").then((r) => r.data),
      api.get("/me/identity").then((r) => r.data),
      api.get("/me/profile").then((r) => r.data),
    ]);
    setTransactions(txs); setBank(b); setIdentity(i); setProfile(p);
  };
  useEffect(() => { load(); }, []);

  const fmtMoney = (a, c) => new Intl.NumberFormat(lang === "de" ? "de-DE" : "en-US", { style: "currency", currency: c, maximumFractionDigits: 0 }).format(Number(a));
  const fmtDateTime = (iso) => new Date(iso).toLocaleString(lang === "de" ? "de-DE" : "en-US", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });
  const idTypeLabel = (v) => (v === "passport" ? t("admin.idType.passport") : v === "residence" ? t("admin.idType.residence") : t("admin.idType.id"));

  const loadByReference = async (reference) => {
    setError(null); setBusy(true);
    try {
      const { data } = await api.get(`/me/transactions/by-reference/${encodeURIComponent(reference.trim().toUpperCase())}`);
      if (!data) { setError(t("client.ref.invalid")); return; }
      setActiveTx(data); setSection("overview");
    } finally { setBusy(false); }
  };

  const notifications = useMemo(() => {
    const meta = { released: { k: "client.step.released", tone: "emerald" }, received: { k: "client.step.received", tone: "emerald" }, sent: { k: "client.step.reported", tone: "amber" }, pending: { k: "client.step.awaiting", tone: "accent" } };
    return transactions.map((tx) => {
      const at = tx.releasedAt ?? tx.receivedAt ?? tx.paymentConfirmedAt ?? tx.createdAt;
      const m = meta[tx.status] ?? meta.pending;
      return { id: String(tx.id), title: t(m.k), description: `${tx.reference} · ${fmtMoney(tx.amount, tx.currency)}`, at, tone: m.tone };
    }).sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [transactions, lang]);

  const auditEvents = useMemo(() => {
    const ev = [];
    for (const tx of transactions) {
      ev.push({ k: "client.stepper.order", reference: tx.reference, at: tx.createdAt });
      if (tx.paymentConfirmedAt) ev.push({ k: "client.step.reported", reference: tx.reference, at: tx.paymentConfirmedAt });
      if (tx.receivedAt) ev.push({ k: "client.step.received", reference: tx.reference, at: tx.receivedAt });
      if (tx.releasedAt) ev.push({ k: "client.step.released", reference: tx.reference, at: tx.releasedAt });
    }
    return ev.sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [transactions]);

  const title = activeTx && section === "overview" ? t("client.pay.title") : t(`portal.section.${section}`);
  const subtitle = t(`portal.section.${section}.sub`);

  return (
    <PortalShell userName={user.name} userRole="client" active={section} notifications={notifications}
      onNavigate={(s) => { setSection(s); setActiveTx(null); }} title={title} subtitle={subtitle}>

      {section === "overview" && (activeTx ? (
        <div className="mx-auto max-w-3xl">
          <ClientPaymentProcess initialTx={activeTx} onReset={() => { setActiveTx(null); setCode(""); load(); }} />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="mx-auto max-w-6xl"><WelcomeVideo /></div>
          <div className="mx-auto max-w-6xl space-y-6">
            {/* Reference lookup */}
            <Card className="premium-card p-6">
              <h2 className="font-serif text-lg font-semibold text-foreground">{t("client.ref.title")}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t("client.ref.subtitle")}</p>
              <form onSubmit={(e) => { e.preventDefault(); loadByReference(code); }} className="mt-4 flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("client.ref.placeholder")} className="pl-9 font-mono uppercase" />
                </div>
                <Button type="submit" disabled={busy || !code}>{t("client.ref.submit")}<ArrowRight className="h-4 w-4" /></Button>
              </form>
              {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5 text-accent" />{t("client.ref.secured")}</p>
            </Card>

            {/* Transactions list */}
            <Card className="premium-card overflow-hidden p-0">
              <div className="border-b border-border px-5 py-4">
                <h2 className="font-medium text-foreground">{t("client.tx.title")}</h2>
                <p className="text-xs text-muted-foreground">{t("client.tx.subtitle")}</p>
              </div>
              {transactions.length === 0 ? <Empty label={t("client.tx.none")} /> : (
                <ul className="divide-y divide-border">
                  {transactions.map((tx) => (
                    <li key={tx.id}>
                      <button onClick={() => loadByReference(tx.reference)} className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-secondary/40">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-foreground">{tx.investorName}</p>
                          <p className="truncate font-mono text-xs text-muted-foreground">{tx.reference}</p>
                        </div>
                        <span className="hidden text-sm font-medium tabular-nums text-foreground sm:block">{fmtMoney(tx.amount, tx.currency)}</span>
                        <StatusBadge status={tx.status} />
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      ))}

      {section === "documents" && <SectionWrap title={t("client.docs.title")} Icon={FileText}><ClientDocumentsSection /></SectionWrap>}

      {section === "signatures" && (
        <SectionWrap title={t("sighist.title")} Icon={CheckCircle2}>
          {transactions.length === 0 ? <Empty label={t("section.empty")} /> : (
            <ul className="divide-y divide-border">
              {transactions.map((tx) => (
                <li key={tx.id} className="flex items-center gap-4 px-5 py-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-muted-foreground">{(tx.investorName || "?").slice(0, 2).toUpperCase()}</span>
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-foreground">{tx.investorName}</p><p className="truncate font-mono text-xs text-muted-foreground">{tx.reference}</p></div>
                  <span className="text-xs text-muted-foreground">{fmtDateTime(tx.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionWrap>
      )}

      {section === "verification" && (
        <SectionWrap title={t("client.identity.title")} Icon={IdCard} verified={!!identity} verifiedLabel={t("client.identity.verified")}>
          {identity ? (
            <div className="px-5 py-5">
              <p className="text-sm text-muted-foreground">{t("client.identity.desc")}</p>
              <dl className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
                <Field label={t("client.identity.fullName")} value={identity.fullName} />
                <Field label={t("client.identity.idType")} value={idTypeLabel(identity.idType)} />
                <Field label={t("client.identity.idNumber")} value={identity.idNumber} mono />
                <Field label={t("client.identity.birthDate")} value={identity.birthDate} />
                <Field label={t("client.identity.nationality")} value={identity.nationality} />
              </dl>
            </div>
          ) : <Empty label={t("section.empty")} />}
        </SectionWrap>
      )}

      {section === "audit" && (
        <SectionWrap title={t("section.audit.title")} Icon={Clock}>
          {auditEvents.length === 0 ? <Empty label={t("section.empty")} /> : (
            <ul className="space-y-0 px-5 py-4">
              {auditEvents.map((ev, i, arr) => (
                <li key={i} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent/15 text-accent"><CheckCircle2 className="h-4 w-4" /></span>
                    {i < arr.length - 1 && <span className="w-px flex-1 bg-border" />}
                  </div>
                  <div className="pb-6"><p className="text-sm font-medium text-foreground">{t(ev.k)} · <span className="font-mono text-xs text-muted-foreground">{ev.reference}</span></p><p className="mt-0.5 text-xs text-muted-foreground">{fmtDateTime(ev.at)}</p></div>
                </li>
              ))}
            </ul>
          )}
        </SectionWrap>
      )}

      {section === "vault" && (
        <SectionWrap title={bank?.bankName ?? t("section.vault.title")} Icon={Landmark} verified={!!bank} verifiedLabel={t("client.bank.status")}>
          {bank ? (
            <div className="px-5 py-5">
              <p className="text-sm leading-relaxed text-muted-foreground">{t("client.bank.desc")}</p>
              <dl className="mt-6 grid gap-x-8 gap-y-5 sm:grid-cols-2">
                <Field label={t("client.bank.holder")} value={bank.accountHolder} />
                <Field label={t("client.bank.bank")} value={bank.bankName} />
                <Field label={t("client.bank.iban")} value={bank.iban.replace(/(.{4})/g, "$1 ").trim()} mono />
                <Field label={t("client.bank.bic")} value={bank.bic ?? "—"} mono />
              </dl>
              <div className="mt-6 flex items-center gap-2 border-t border-border pt-4 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5 text-accent" />{t("client.secured")}</div>
            </div>
          ) : <Empty label={t("section.empty")} />}
        </SectionWrap>
      )}

      {section === "settings" && (
        <SectionWrap title={t("section.settings.acct")} Icon={ShieldCheck}>
          <div className="px-5 py-5">
            <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
              <Field label={t("client.bank.holder")} value={user.name} />
              <Field label={t("auth.email")} value={user.email} />
              {profile?.institutionName && <Field label={t("portal.role.client")} value={profile.institutionName} />}
            </dl>
          </div>
        </SectionWrap>
      )}

      {section === "support" && <SectionWrap title={t("support.title")} Icon={Headphones}><SupportContacts /></SectionWrap>}
    </PortalShell>
  );
}
